import React, { useState, useEffect, useRef } from 'react';
import { Save, Layers, Search, X, Check, Sparkles, Wrench, Droplets } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import fetchWithAuth from '../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

const MATERIAL_TYPES = [
  'Sheet', 'Plate', 'Pipe', 'Tube', 'Round_Bar', 'Flat_Bar', 'Hex_Bar',
  'Square_Bar', 'Angle', 'Channel', 'Beam', 'Block',
];

/* ------------------------------------------------------------------ */
/* Save As Process Template Popover                                    */
/* Collects current line's fab ops + surface treatments, creates a     */
/* Process Library template via POST /api/v1/process-library/templates */
/* ------------------------------------------------------------------ */
const SaveTemplatePopover = ({ line, materialForm, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [materialTypes, setMaterialTypes] = useState(new Set(materialForm ? [materialForm] : []));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 100); }, []);

  // Extract process codes from current line
  const fabCodes = (line.fabrication_operations || [])
    .map(op => op.operation_code)
    .filter(Boolean);
  const sftCodes = (line.applied_process_rules || [])
    .filter(r => r.is_selected)
    .map(r => r.rule_code)
    .filter(Boolean);

  const toggleMaterial = (type) => {
    setMaterialTypes(prev => {
      const next = new Set(prev);
      next.has(type) ? next.delete(type) : next.add(type);
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Template name is required'); return; }
    if (fabCodes.length === 0 && sftCodes.length === 0) { toast.error('No operations or treatments to save'); return; }
    setSaving(true);
    try {
      const tk = getToken();
      const res = await fetchWithAuth(`${API}/api/v1/process-library/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({
          template_name: name.trim(),
          description: desc.trim() || undefined,
          fabrication_process_codes: fabCodes,
          surface_treatment_codes: sftCodes,
          applicable_material_types: Array.from(materialTypes),
        }),
      });
      if (res.ok) {
        toast.success(`Template "${name}" saved (${fabCodes.length} fab, ${sftCodes.length} sft)`);
        onSaved?.();
        onClose();
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Save failed');
      }
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  return (
    <div className="border border-emerald-600/40 rounded-lg bg-slate-900 p-3 mt-2 shadow-xl animate-in fade-in duration-150"
      data-testid="save-template-popover">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-emerald-400 font-semibold uppercase tracking-wider flex items-center gap-1">
          <Save className="w-3 h-3" /> Save as Process Template
        </span>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300"><X className="w-3.5 h-3.5" /></button>
      </div>

      <div className="space-y-2">
        <Input ref={inputRef} value={name} onChange={e => setName(e.target.value)}
          placeholder="Template name *"
          className="h-7 text-[11px] bg-slate-800 border-slate-600 text-white"
          data-testid="save-template-name-input" />
        <Input value={desc} onChange={e => setDesc(e.target.value)}
          placeholder="Description (optional)"
          className="h-7 text-[11px] bg-slate-800 border-slate-600 text-white" />

        {/* Material Types */}
        <div>
          <Label className="text-[9px] text-slate-500 uppercase tracking-wider mb-1 block">Applicable Material Types</Label>
          <div className="flex flex-wrap gap-1">
            {MATERIAL_TYPES.map(t => (
              <button key={t} onClick={() => toggleMaterial(t)}
                className={`px-1.5 py-0.5 rounded text-[9px] transition-colors ${
                  materialTypes.has(t)
                    ? 'bg-cyan-600/30 text-cyan-300 border border-cyan-500/40'
                    : 'bg-slate-800 text-slate-500 border border-slate-700 hover:text-slate-300'
                }`}
                data-testid={`save-tmpl-mat-${t}`}>
                {t.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Summary of what will be saved */}
        <div className="border border-slate-700/40 rounded p-2 bg-slate-800/30 space-y-1">
          <div className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold">Will save from this part:</div>
          {fabCodes.length > 0 && (
            <div className="flex items-center gap-1 text-[10px]">
              <Wrench className="w-2.5 h-2.5 text-orange-400" />
              <span className="text-orange-300">{fabCodes.length} fabrication operation{fabCodes.length !== 1 ? 's' : ''}</span>
            </div>
          )}
          {sftCodes.length > 0 && (
            <div className="flex items-center gap-1 text-[10px]">
              <Droplets className="w-2.5 h-2.5 text-violet-400" />
              <span className="text-violet-300">{sftCodes.length} surface treatment{sftCodes.length !== 1 ? 's' : ''}</span>
            </div>
          )}
          {fabCodes.length === 0 && sftCodes.length === 0 && (
            <div className="text-[10px] text-slate-500 italic">No operations or treatments on this part</div>
          )}
        </div>

        <div className="flex gap-2">
          <Button size="sm" className="flex-1 h-6 text-[10px] bg-emerald-600 hover:bg-emerald-500 text-white"
            onClick={handleSave} disabled={saving || !name.trim() || (fabCodes.length === 0 && sftCodes.length === 0)}
            data-testid="save-template-confirm-btn">
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
          <Button size="sm" variant="outline" className="h-6 text-[10px] border-slate-600 text-slate-300" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Apply Process Template Popover                                      */
/* Fetches from /api/v1/process-library/templates, filters by material */
/* Applies via /api/v1/process-library/templates/{id}/apply            */
/* ------------------------------------------------------------------ */
const ApplyTemplatePopover = ({ bomId, revisionId, lineId, materialForm, onRefresh, onClose, onBusy }) => {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      const tk = getToken();
      try {
        const res = await fetchWithAuth(
          `${API}/api/v1/process-library/templates?limit=100`,
          { headers: { Authorization: `Bearer ${tk}` } }
        );
        if (res.ok) {
          const d = await res.json();
          setTemplates(d.items || []);
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // Score by material relevance
  const scored = templates.map(t => {
    let score = 0;
    const matTypes = t.applicable_material_types || [];
    if (materialForm && matTypes.includes(materialForm)) score += 10;
    if (matTypes.length === 0) score += 2; // universal templates get minor boost
    return { ...t, _score: score };
  }).sort((a, b) => b._score - a._score);

  const filtered = scored.filter(t =>
    t.template_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.template_code?.toLowerCase().includes(search.toLowerCase()) ||
    t.description?.toLowerCase().includes(search.toLowerCase())
  );

  const recommended = filtered.filter(t => t._score >= 10);
  const others = filtered.filter(t => t._score < 10);

  const applyTemplate = async (tmpl) => {
    setApplying(tmpl.template_id);
    onBusy?.('applying');
    try {
      const tk = getToken();
      const res = await fetchWithAuth(
        `${API}/api/v1/process-library/templates/${tmpl.template_id}/apply`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
          body: JSON.stringify({
            bom_id: bomId,
            revision_id: revisionId,
            line_ids: [lineId],
          }),
        }
      );
      if (res.ok) {
        const result = await res.json();
        const fabCount = tmpl.fabrication_process_codes?.length || 0;
        const sftCount = tmpl.surface_treatment_codes?.length || 0;
        toast.success(`Applied "${tmpl.template_name}" (${fabCount} fab, ${sftCount} sft)`);
        onRefresh();
        onClose();
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Apply failed');
      }
    } catch { toast.error('Apply failed'); }
    setApplying(null);
    onBusy?.(null);
  };

  const TemplateCard = ({ t, highlight }) => (
    <button onClick={() => applyTemplate(t)} disabled={applying === t.template_id}
      className={`w-full text-left px-2.5 py-2 rounded-md transition-colors disabled:opacity-50 ${
        highlight ? 'bg-emerald-900/20 border border-emerald-600/30 hover:bg-emerald-900/30' : 'hover:bg-slate-800/50 border border-transparent'
      }`}
      data-testid={`apply-tmpl-${t.template_id}`}>
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-slate-200 font-medium flex-1">{t.template_name}</span>
      </div>
      <div className="text-[9px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
        <span className="font-mono">{t.template_code}</span>
        {t.fabrication_process_codes?.length > 0 && (
          <span className="text-orange-400/60">{t.fabrication_process_codes.length} fab</span>
        )}
        {t.surface_treatment_codes?.length > 0 && (
          <span className="text-violet-400/60">{t.surface_treatment_codes.length} sft</span>
        )}
        {t.applicable_material_types?.length > 0 && (
          <span className="text-cyan-400/60">{t.applicable_material_types.join(', ')}</span>
        )}
      </div>
      {t.description && <div className="text-[8px] text-slate-600 mt-0.5 truncate">{t.description}</div>}
      {highlight && (
        <div className="text-[8px] text-emerald-400/70 mt-0.5 flex items-center gap-1">
          <Sparkles className="w-2.5 h-2.5" /> Matches material: {materialForm}
        </div>
      )}
    </button>
  );

  return (
    <div className="border border-emerald-600/40 rounded-lg bg-slate-900 p-3 mt-2 shadow-xl animate-in fade-in duration-150"
      data-testid="apply-template-popover">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-emerald-400 font-semibold uppercase tracking-wider flex items-center gap-1">
          <Layers className="w-3 h-3" /> Apply Process Template
        </span>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300"><X className="w-3.5 h-3.5" /></button>
      </div>

      <div className="relative mb-2">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
        <Input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search templates..."
          className="h-7 text-[11px] bg-slate-800 border-slate-600 text-white pl-7"
          data-testid="apply-template-search" />
      </div>

      <div className="max-h-[220px] overflow-y-auto space-y-1">
        {loading ? (
          <div className="text-[10px] text-slate-500 text-center py-4">Loading templates...</div>
        ) : filtered.length === 0 ? (
          <div className="text-[10px] text-slate-500 text-center py-4">
            {templates.length === 0 ? 'No templates yet. Create one from the Process Library or save from a part.' : 'No matching templates'}
          </div>
        ) : (
          <>
            {recommended.length > 0 && (
              <div>
                <div className="text-[9px] text-emerald-400 font-semibold uppercase tracking-wider px-1 py-0.5 mb-1">
                  Recommended for {materialForm}
                </div>
                {recommended.map(t => <TemplateCard key={t.template_id} t={t} highlight />)}
              </div>
            )}
            {others.length > 0 && (
              <div>
                {recommended.length > 0 && (
                  <div className="text-[9px] text-slate-500 font-semibold uppercase tracking-wider px-1 py-0.5 mt-1.5 mb-1">
                    All Templates
                  </div>
                )}
                {others.map(t => <TemplateCard key={t.template_id} t={t} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Template Suggestion Banner                                          */
/* Shows when a Make part has no ops/treatments and a matching         */
/* template exists for the part's material type                        */
/* ------------------------------------------------------------------ */
const TemplateSuggestionBanner = ({ materialForm, bomId, revisionId, lineId, onRefresh, onBusy }) => {
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!materialForm) { setLoading(false); return; }
    (async () => {
      const tk = getToken();
      try {
        const res = await fetchWithAuth(
          `${API}/api/v1/process-library/templates?material_type=${materialForm}&limit=1`,
          { headers: { Authorization: `Bearer ${tk}` } }
        );
        if (res.ok) {
          const d = await res.json();
          if (d.items?.length > 0) setSuggestion(d.items[0]);
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [materialForm]);

  if (loading || dismissed || !suggestion) return null;

  const handleApply = async () => {
    setApplying(true);
    onBusy?.('applying');
    try {
      const tk = getToken();
      const res = await fetchWithAuth(
        `${API}/api/v1/process-library/templates/${suggestion.template_id}/apply`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
          body: JSON.stringify({
            bom_id: bomId,
            revision_id: revisionId,
            line_ids: [lineId],
          }),
        }
      );
      if (res.ok) {
        toast.success(`Applied template: ${suggestion.template_name}`);
        onRefresh();
        setDismissed(true);
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Apply failed');
      }
    } catch { toast.error('Apply failed'); }
    setApplying(false);
    onBusy?.(null);
  };

  return (
    <div className="border border-emerald-600/30 rounded-lg p-2.5 bg-emerald-900/10 animate-in fade-in duration-200"
      data-testid="template-suggestion-banner">
      <div className="flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] text-emerald-400 font-semibold">Suggested Template for {materialForm}</div>
          <div className="text-[10px] text-slate-300 truncate">{suggestion.template_name}</div>
          <div className="text-[9px] text-slate-500">
            {suggestion.fabrication_process_codes?.length || 0} fab, {suggestion.surface_treatment_codes?.length || 0} sft
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button size="sm" className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-500"
            onClick={handleApply} disabled={applying} data-testid="suggestion-apply-btn">
            {applying ? '...' : 'Apply'}
          </Button>
          <Button size="sm" variant="ghost" className="h-6 text-[10px] text-slate-500"
            onClick={() => setDismissed(true)} data-testid="suggestion-dismiss-btn">
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Manufacturing Template Actions (main export)                        */
/* Unified: uses Process Library template system                       */
/* ------------------------------------------------------------------ */
export const ManufacturingTemplateActions = ({
  line, bomId, revisionId, onRefresh, isDraft = true, materialProfile, onBusy
}) => {
  const [showSave, setShowSave] = useState(false);
  const [showApply, setShowApply] = useState(false);

  if (!line || line.make_buy !== 'Make' || line.is_assembly) return null;

  const hasOpsOrTreatments = (line.fabrication_operations?.length > 0) ||
    (line.applied_process_rules?.some(r => r.is_selected));
  const materialForm = materialProfile?.material_form;

  return (
    <div className="space-y-2" data-testid="mfg-template-section">
      {/* Auto-suggest banner — shows when part has no ops/treatments and a matching template exists */}
      {isDraft && materialForm && !hasOpsOrTreatments && (
        <TemplateSuggestionBanner
          materialForm={materialForm}
          bomId={bomId}
          revisionId={revisionId}
          lineId={line.line_id}
          onRefresh={onRefresh}
          onBusy={onBusy}
        />
      )}

      {/* Save / Apply buttons */}
      {isDraft && !showSave && !showApply && (
        <div className="flex gap-1.5" data-testid="mfg-template-actions">
          {hasOpsOrTreatments && (
            <Button size="sm" variant="outline" onClick={() => { setShowSave(true); setShowApply(false); }}
              className="flex-1 h-6 text-[10px] border-emerald-600/30 text-emerald-400 hover:bg-emerald-900/20 hover:text-emerald-300"
              data-testid="mfg-save-template-btn">
              <Save className="w-3 h-3 mr-1" /> Save as Template
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => { setShowApply(true); setShowSave(false); }}
            className="flex-1 h-6 text-[10px] border-emerald-600/30 text-emerald-400 hover:bg-emerald-900/20 hover:text-emerald-300"
            data-testid="mfg-apply-template-btn">
            <Layers className="w-3 h-3 mr-1" /> Apply Template
          </Button>
        </div>
      )}

      {showSave && (
        <SaveTemplatePopover
          line={line}
          materialForm={materialForm}
          onClose={() => setShowSave(false)}
          onSaved={() => setShowSave(false)}
        />
      )}

      {showApply && (
        <ApplyTemplatePopover
          bomId={bomId} revisionId={revisionId} lineId={line.line_id}
          materialForm={materialForm}
          onRefresh={onRefresh} onClose={() => setShowApply(false)}
          onBusy={onBusy}
        />
      )}
    </div>
  );
};
